import { Fragment, useEffect, useRef, useState } from "react";

import { Menu } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PaperClipIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import {
  IInboxAttachment,
  IInboxConversation,
  IInboxMessage,
  IInboxUser,
  InboxStatus,
  InboxType,
} from "../../models/messagesInbox/IInboxConversation";
import { IInboxTemplate } from "../../stubs/messagesInboxSamples";
import classNames from "../../utils/classNames";
import { decodeHtml } from "../../utils/decodeHtml";
import { splitByRegs } from "../../utils/detectRegs";
import { ALLOWED_ATTACHMENT_TYPES, attachmentAccept, checkAttachments } from "../../utils/inboxAttachments";
import { messageTimeLabel } from "../../utils/inboxDates";
import { noReplyNeededMessage, unansweredBuyerMessages } from "../../utils/noReplyNeeded";
import { trackingLink } from "../../utils/trackingLink";
import { ChannelBadge, IInboxNotice, InboxError, InboxLoading, InboxNotice, PlateChip, useInboxLive } from "./InboxBits";

export const STATUS_LABELS: Record<InboxStatus, string> = {
  needs_reply: "Needs reply",
  waiting_on_buyer: "Waiting on buyer",
  no_reply_needed: "No reply needed",
};

/** What the buyer wants, as the type picker and chip call it. */
export const TYPE_LABELS: Record<InboxType, string> = {
  cancellation: "Cancellation",
  address_change: "Address change",
  amendment: "Order change",
  fitment: "Fitment question",
  wheres_my_order: "Where's my order",
  collection: "Collection",
  offer: "Offer",
  return: "Return / refund",
  thanks: "Thanks, no reply needed",
  other: "Other",
};

/**
 * AI's reading of what the buyer wants, as a picker: the type it chose, with "(AI, unsure)" when it
 * wasn't confident, and any other type a click away. Only drawn once the API has triaged the
 * conversation (typeConfidence set), so an inbox without the AI flag looks as it did.
 */
function TypeChip({ c, onType }: { c: IInboxConversation; onType: (type: InboxType) => void }) {
  const { t } = useTranslation("messages");
  if (!c.typeConfidence) return null;
  const unsure = c.typeConfidence === "low" || c.typeConfidence === "medium";
  const byUser = c.typeConfidence === "user";
  return (
    <label
      className={classNames(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        unsure ? "border-amber-200 bg-amber-50 text-amber-800" : "border-gray-200 bg-gray-50 text-gray-700"
      )}
      title={byUser ? t("Set by hand") : t("What AI thinks the buyer wants – change it if it's wrong")}
    >
      {!byUser && <SparklesIcon className="h-3.5 w-3.5 shrink-0" />}
      <select
        value={c.type}
        onChange={(e) => onType(e.target.value as InboxType)}
        className="cursor-pointer appearance-none border-0 bg-transparent bg-none p-0 pr-4 text-[11px] font-medium focus:ring-0"
      >
        {(Object.keys(TYPE_LABELS) as InboxType[]).map((type) => (
          <option key={type} value={type}>
            {t(TYPE_LABELS[type])}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="-ml-4 h-3 w-3 shrink-0" />
      {unsure && <span className="ml-0.5 opacity-80">{t("(AI, unsure)")}</span>}
    </label>
  );
}

const clock = (iso: string) => messageTimeLabel(iso);

function MessageText({ body, onReg }: { body: string; onReg?: (reg: string) => void }) {
  return (
    <>
      {splitByRegs(decodeHtml(body)).map((segment, i) =>
        segment.reg ? <PlateChip key={i} reg={segment.reg} onClick={onReg ? () => onReg(segment.reg as string) : undefined} /> : <Fragment key={i}>{segment.text}</Fragment>
      )}
    </>
  );
}

function NoteText({ body, users }: { body: string; users: IInboxUser[] }) {
  const names = users.map((u) => u.forename).join("|");
  const parts = names ? body.split(new RegExp(`(@(?:${names}))`, "g")) : [body];
  return (
    <>
      {parts.map((part, i) => (part.startsWith("@") ? <strong key={i} className="font-semibold text-violet-900">{part}</strong> : <Fragment key={i}>{part}</Fragment>))}
    </>
  );
}

/** A system line with the order's tracking number turned into a link to the courier's page. */
function withTrackingLink(text: string, tracking?: { number: string; href?: string }) {
  if (!tracking?.href || !text.includes(tracking.number)) return text;
  const [before, ...rest] = text.split(tracking.number);
  return (
    <>
      {before}
      <a href={tracking.href} target="_blank" rel="noreferrer" className="font-mono font-medium text-primary-700 hover:underline">
        {tracking.number}
      </a>
      {rest.join(tracking.number)}
    </>
  );
}

/** The photos on a message: the buyer's from eBay, or ours sent with a reply. Each opens full size. */
function Photos({ attachments, hasText }: { attachments?: IInboxAttachment[]; hasText: boolean }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={classNames("flex flex-wrap gap-1.5", hasText && "mt-2")}>
      {attachments.map((a) => (
        <a key={a.url} href={a.url} target="_blank" rel="noreferrer">
          <img src={a.url} alt={a.name} className="h-24 w-24 rounded-lg object-cover" />
        </a>
      ))}
    </div>
  );
}

function TimelineItem({
  message,
  buyerName,
  usersById,
  users,
  onReg,
  tracking,
  onDelete,
}: {
  message: IInboxMessage;
  buyerName: string;
  usersById: Record<string, IInboxUser>;
  /** Live data: removes this note. */
  onDelete?: () => void;
  users: IInboxUser[];
  onReg: (reg: string) => void;
  tracking?: { number: string; href?: string };
}) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const author = message.authorId ? usersById[message.authorId] : undefined;
  // Live data names the author (from the API); sample data knows them by id.
  const authorName = author ? (author.role === "Warehouse" ? t("{{name}} (warehouse)", { name: author.forename }) : author.forename) : message.authorName ?? "";

  if (message.direction === "system") {
    return (
      <div className="flex items-center gap-3 py-1 text-[12px] text-gray-500">
        <span className="h-px flex-1 bg-gray-200" />
        <span>
          {withTrackingLink(decodeHtml(message.body), tracking)} · {clock(message.sentAt)}
        </span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>
    );
  }

  if (message.direction === "buyer") {
    return (
      <div className="max-w-[75%]">
        <div className="whitespace-pre-line rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[14px] leading-relaxed text-gray-900">
          <MessageText body={message.body} onReg={onReg} />
          <Photos attachments={message.attachments} hasText={!!message.body} />
        </div>
        <p className="mt-1 text-[11px] text-gray-500">
          {buyerName} · {clock(message.sentAt)}
        </p>
      </div>
    );
  }

  if (message.direction === "note") {
    return (
      <div className="ml-auto max-w-[75%]">
        <div className="rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2.5 text-[14px] leading-relaxed text-gray-900">
          <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-violet-700">{t("Internal note")}</p>
          <NoteText body={message.body} users={users} />
        </div>
        <p className="mt-1 text-right text-[11px] text-gray-500">
          {authorName || t("You")} · {clock(message.sentAt)}
          {onDelete && (
            <button type="button" onClick={onDelete} className="ml-2 text-violet-700 hover:underline">
              {t("Remove")}
            </button>
          )}
        </p>
      </div>
    );
  }

  const isAuto = message.direction === "auto";
  return (
    <div className="ml-auto max-w-[75%]">
      <div
        className={classNames(
          "whitespace-pre-line rounded-xl px-3.5 py-2.5 text-[14px] leading-relaxed",
          // Our replies use the customer's own brand colour (set per customer in Betty).
          isAuto ? "border border-dashed border-primary-300 bg-primary-50 text-gray-900" : "bg-primary-600 text-white",
          message.previewOnly && "opacity-80"
        )}
      >
        {isAuto && <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700">{t("Automatic reply · sent by Wilma")}</p>}
        {decodeHtml(message.body)}
        <Photos attachments={message.attachments} hasText={!!message.body} />
      </div>
      <p className="mt-1 text-right text-[11px] text-gray-500">
        {message.previewOnly ? (
          <span className="font-medium text-amber-700">{isAuto ? t("Preview only – not sent to the buyer") : t("Preview only – not sent to eBay or PartPro")}</span>
        ) : (
          <>
            {/* Live replies don't say who sent them yet, so they're just "Sent". */}
            {isAuto ? t("Sent automatically") : authorName || (live ? t("Sent") : t("You"))} · {clock(message.sentAt)}
            {message.pending && <span className="ml-1 text-gray-400">{t("(just sent)")}</span>}
          </>
        )}
      </p>
    </div>
  );
}

interface ThreadViewProps {
  conversation: IInboxConversation;
  usersById: Record<string, IInboxUser>;
  users: IInboxUser[];
  position: { index: number; total: number };
  onPrev: () => void;
  onNext: () => void;
  /** Undoes "No reply needed". Status is otherwise automatic: reply → Sent, buyer writes → Needs reply. */
  onNeedsReply: () => void;
  onReg: (reg: string) => void;
  draft: string;
  onDraft: (draft: string) => void;
  attachments: IInboxAttachment[];
  onAttach: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
  noteMode: boolean;
  onNoteMode: (noteMode: boolean) => void;
  templates: IInboxTemplate[];
  onTemplate: (template: IInboxTemplate) => void;
  onSend: () => void;
  onNoReplyNeeded: () => void;
  /** Corrects what the buyer wants (AI's type) when it read the message wrong. */
  onType: (type: InboxType) => void;
  /** Live data: removes a note from the thread. */
  onDeleteNote?: (noteId: string) => void;
  replyByText: string;
  /** Live data: the thread is fetched from eBay / PartPro when the conversation opens. */
  threadStatus?: { loading: boolean; error?: string; onRetry: () => void };
  /** Live data: a reply is on its way. */
  sending?: boolean;
  /** Live data: a cancel or refund is in flight, so stay on this conversation until it answers. */
  busy?: boolean;
  /** Live data: what happened to the last reply (sent, blocked or refused). */
  sendNotice?: IInboxNotice;
}

export default function ThreadView({
  conversation: c,
  usersById,
  users,
  position,
  onPrev,
  onNext,
  onNeedsReply,
  onReg,
  draft,
  onDraft,
  attachments,
  onAttach,
  onRemoveAttachment,
  noteMode,
  onNoteMode,
  templates,
  onTemplate,
  onSend,
  onNoReplyNeeded,
  onType,
  onDeleteNote,
  replyByText,
  threadStatus,
  sending = false,
  busy = false,
  sendNotice,
}: ThreadViewProps) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const timelineEnd = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [attachError, setAttachError] = useState<string>();
  const [confirmNoReply, setConfirmNoReply] = useState(false);

  useEffect(() => {
    setAttachError(undefined);
    setConfirmNoReply(false);
  }, [c.id]);

  const unanswered = unansweredBuyerMessages(c);
  const tracking = c.order?.trackingNumber ? { number: c.order.trackingNumber, href: trackingLink(c.order) } : undefined;
  // "No reply needed" and notes are stored by the API; an older API that can't is told apart by the
  // row (wilmaStatusSupported), and then the buttons stay hidden rather than fail.
  const wilmaStatus = !live || !!c.wilmaStatusSupported;
  const looksLikeThanks = wilmaStatus && c.status === "needs_reply" && (c.type === "thanks" || !!noReplyNeededMessage(c));
  const partLabel = c.part ? `${c.part.shortTitle} · ${t("Tag {{tag}}", { tag: c.part.tagNumber })}` : [c.itemTitle, c.partTagNumber && t("Tag {{tag}}", { tag: c.partTagNumber })].filter(Boolean).join(" · ");

  function noReplyNeeded() {
    // More than one message since our last reply: make sure an earlier question isn't missed.
    if (unanswered.length > 1 && !confirmNoReply) setConfirmNoReply(true);
    else onNoReplyNeeded();
  }

  function pickFiles(list: FileList | null) {
    const { accepted, rejected } = checkAttachments(Array.from(list ?? []), c.channel);
    if (accepted.length) onAttach(accepted as File[]);
    setAttachError(
      rejected.length
        ? t("{{files}} can't be sent – {{channel}} messages only take {{types}}.", {
            files: rejected.join(", "),
            channel: c.channel === "ebay" ? "eBay" : "PartPro",
            types: t(ALLOWED_ATTACHMENT_TYPES[c.channel].label),
          })
        : undefined
    );
  }

  useEffect(() => {
    timelineEnd.current?.scrollIntoView({ block: "end" });
  }, [c.id, c.messages.length]);


  return (
    <div className="flex min-w-0 flex-1 flex-col bg-gray-50">
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <button type="button" onClick={onPrev} disabled={busy || position.index <= 0} title={t("Previous conversation")} className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button type="button" onClick={onNext} disabled={busy || position.index >= position.total - 1} title={t("Next conversation")} className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
          <ChannelBadge channel={c.channel} />
          <span className="text-[15px] font-semibold text-gray-900">{c.buyer.username}</span>
          <span className="min-w-0 truncate text-[13px] text-gray-500">
            {c.order ? `· ${t("Order {{number}}", { number: c.order.orderNumber })}` : ""} {partLabel ? `· ${partLabel}` : ""}
          </span>
          <TypeChip c={c} onType={onType} />
        </div>
        {c.aiSummary && (
          <p className="mt-2 flex items-start gap-2 text-[13px] text-gray-700">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
            <span>
              {c.aiSummary}
              {!live && <span className="ml-1.5 text-[11px] text-gray-400">{t("(sample summary)")}</span>}
            </span>
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
        {threadStatus?.error && <InboxError text={threadStatus.error} onRetry={threadStatus.onRetry} />}
        {threadStatus?.loading && c.messages.length === 0 && <InboxLoading text={t("Fetching the messages from {{channel}}…", { channel: c.channel === "ebay" ? "eBay" : "PartPro" })} />}
        {threadStatus && !threadStatus.loading && !threadStatus.error && c.messages.length === 0 && (
          <p className="py-12 text-center text-[13px] text-gray-500">{t("No messages in this conversation.")}</p>
        )}
        {c.messages.map((m) => (
          <TimelineItem
            key={m.id}
            message={m}
            buyerName={c.buyer.username}
            usersById={usersById}
            users={users}
            onReg={onReg}
            tracking={tracking}
            onDelete={onDeleteNote && m.direction === "note" ? () => onDeleteNote(m.id) : undefined}
          />
        ))}
        <div ref={timelineEnd} />
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        {c.status === "no_reply_needed" && (
          <div className="mb-2 flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-[13px] text-gray-700">
            <span className="flex-1">{t("Marked as no reply needed.")}</span>
            <button type="button" onClick={onNeedsReply} className="rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-800 hover:bg-gray-50">
              {t("Move back to Needs reply")}
            </button>
          </div>
        )}
        {c.status === "needs_reply" && (looksLikeThanks || confirmNoReply) && (
          <div className={classNames("mb-2 flex items-center gap-3 rounded-lg px-3 py-2 text-[13px]", confirmNoReply ? "bg-amber-50 text-amber-900" : "bg-gray-50 text-gray-700")}>
            <span className="flex-1">
              {confirmNoReply
                ? t("{{name}} has sent {{count}} messages since your last reply. Check you haven't missed a question first.", { name: c.buyer.username, count: unanswered.length })
                : t("Looks like the buyer's just saying thanks.")}
            </span>
            {confirmNoReply && (
              <button type="button" onClick={() => setConfirmNoReply(false)} className="rounded-md px-2 py-1 text-gray-700 hover:bg-white">
                {t("Cancel")}
              </button>
            )}
            <button type="button" onClick={onNoReplyNeeded} className="rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-800 hover:bg-gray-50">
              {confirmNoReply ? t("Yes, no reply needed") : t("No reply needed")}
            </button>
          </div>
        )}
        <div className={classNames("rounded-xl border", noteMode ? "border-violet-300 bg-violet-50" : "border-gray-200 bg-white")}>
          <textarea
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            rows={4}
            placeholder={noteMode ? t("Internal note – the buyer never sees this. Use @Name to notify someone.") : t("Reply to {{name}}…", { name: c.buyer.username })}
            className="block w-full resize-y rounded-t-xl border-0 bg-transparent px-3.5 py-2.5 text-[14px] focus:ring-0"
          />
          {!noteMode && attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 px-3.5 pb-2">
              {attachments.map((a, i) => (
                <div key={a.url} className="relative">
                  <img src={a.url} alt={a.name} title={a.name} className="h-14 w-14 rounded-md border border-gray-200 object-cover" />
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(i)}
                    title={t("Remove {{name}}", { name: a.name })}
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-gray-900 p-0.5 text-white hover:bg-gray-700"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1 border-t border-gray-100 px-2 py-1.5">
            {templates.length > 0 && (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-1 rounded-md px-2 py-1 text-[13px] text-gray-700 hover:bg-gray-100">
                  {t("Templates")} <ChevronDownIcon className="h-3.5 w-3.5" />
                </Menu.Button>
                <Menu.Items className="absolute bottom-full left-0 z-30 mb-1 w-60 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                  {templates.map((tpl) => (
                    <Menu.Item key={tpl.name}>
                      {({ active }) => (
                        <button type="button" onClick={() => onTemplate(tpl)} className={classNames("block w-full px-3 py-1.5 text-left text-[13px] text-gray-700", active && "bg-gray-50")}>
                          {tpl.name}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
            )}
            {!live && (
              <button type="button" disabled title={t("AI suggested reply – not in this preview")} className="rounded-md p-1.5 text-gray-400">
                <SparklesIcon className="h-4 w-4" />
              </button>
            )}
            {/* Photos go up first (POST attachments) and the reply names them; an API that predates that is told apart by the row. */}
            {!noteMode && wilmaStatus && (
              <>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  title={t("Attach photos ({{types}})", { types: t(ALLOWED_ATTACHMENT_TYPES[c.channel].label) })}
                  className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
                >
                  <PaperClipIcon className="h-4 w-4" />
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={attachmentAccept(c.channel)}
                  className="hidden"
                  onChange={(e) => {
                    pickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </>
            )}
            {wilmaStatus && (
              <label className="ml-1 flex cursor-pointer items-center gap-1.5 text-[13px] text-gray-700">
                <input type="checkbox" checked={noteMode} onChange={(e) => onNoteMode(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                {t("Note")}
              </label>
            )}

            <div className="ml-auto flex items-center gap-2">
              {wilmaStatus && !noteMode && c.status === "needs_reply" && (
                <button type="button" onClick={noReplyNeeded} className="rounded-md px-2 py-1 text-[13px] text-gray-700 hover:bg-gray-100">
                  {t("No reply needed")}
                </button>
              )}
              {c.status === "needs_reply" && <span className="hidden text-[12px] text-gray-500 lg:inline">{t("Reply by {{time}}", { time: replyByText })}</span>}
              <button
                type="button"
                onClick={onSend}
                disabled={sending || (!draft.trim() && (noteMode || attachments.length === 0))}
                className={classNames(
                  "rounded-lg px-4 py-1.5 text-[13px] font-medium text-white disabled:opacity-50",
                  noteMode ? "bg-violet-600 hover:bg-violet-700" : "bg-primary-600 hover:bg-primary-700"
                )}
              >
                {noteMode ? (sending ? t("Adding…") : t("Add note")) : sending ? t("Sending…") : t("Send")}
              </button>
            </div>
          </div>
        </div>
        {attachError && <p className="mt-1.5 text-[12px] text-red-700">{attachError}</p>}
        {live
          ? sendNotice && <InboxNotice notice={sendNotice} className="mt-1.5" />
          : !noteMode && <p className="mt-1.5 text-[11px] text-gray-400">{t("Preview: Send adds your reply to this screen and moves it to Sent. Nothing is sent to eBay or PartPro.")}</p>}
      </div>
    </div>
  );
}
